import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useNavigate } from "react-router-dom";

import CustomButton from "./CustomButton";
import { closeIconButtonSx } from "../../constants/closeIconButtonSx";
import {
  HOME_SECTION_BAND_PADDING_Y,
  HOME_SECTION_HEADING_BELOW_MARGIN,
  HOME_SECTION_HEADING_SX,
  HOME_SECTION_HORIZONTAL_GUTTER,
} from "../../constants/homePageSections";
import { uiNeutrals } from "../../constants/uiNeutrals";
import { appBackgroundColor } from "../../theme";

const LOOPS_LOGO_SRC = "/assets/logo.png";

const REVIEWS = [
  {
    id: "r-1",
    quote: "Such a time saver for KL office lunches.",
    name: "Hafiz R.",
  },
  {
    id: "r-2",
    quote:
      "Super helpful when I'm tracking deals deadline for different restaurants!",
    name: "Annie Lim",
  },
  {
    id: "r-3",
    quote:
      "The tracking and reminder feature is a lifesaver for student in tight budget weeks!",
    name: "Jun Wei",
  },
];

/** 3×3: partner logos + testimonial tiles with Loops logo layered over blurred copy */
const CAROUSEL_TILES = [
  {
    kind: "logo",
    id: "logo-1",
    src: LOOPS_LOGO_SRC,
    alt: "Partner spotlight",
    bgColor: "#D2B48C",
  },
  {
    kind: "logo",
    id: "logo-2",
    src: LOOPS_LOGO_SRC,
    alt: "Partner spotlight",
    bgColor: "#ADD8E6",
  },
  {
    kind: "testimonial",
    id: "slot-review-1",
    reviewIndex: 0,
  },
  {
    kind: "logo",
    id: "logo-3",
    src: LOOPS_LOGO_SRC,
    alt: "Partner spotlight",
    bgColor: "#90EE90",
  },
  {
    kind: "testimonial",
    id: "slot-review-2",
    reviewIndex: 1,
  },
  {
    kind: "logo",
    id: "logo-4",
    src: LOOPS_LOGO_SRC,
    alt: "Partner spotlight",
    bgColor: "#FFB6C1",
  },
  {
    kind: "logo",
    id: "logo-5",
    src: LOOPS_LOGO_SRC,
    alt: "Partner spotlight",
    bgColor: "#DDA0DD",
  },
  {
    kind: "testimonial",
    id: "slot-review-3",
    reviewIndex: 2,
  },
  {
    kind: "logo",
    id: "logo-6",
    src: LOOPS_LOGO_SRC,
    alt: "Partner spotlight",
    bgColor: "#FFE4B5",
  },
];

const cardShellSx = {
  /** Shrink below 115px/160px on narrow viewports so the row never overflows (Home uses overflow-x: hidden). */
  width: { xs: "min(100%, 115px)", md: "min(100%, 160px)" },
  aspectRatio: "1",
  borderRadius: "12px",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
  transition: "filter 0.2s ease, box-shadow 0.2s ease",
};

const reviewNavCircleSx = {
  flexShrink: 0,
  width: 44,
  height: 44,
  minWidth: 44,
  padding: 0,
  borderRadius: "50%",
  bgcolor: uiNeutrals.surface,
  border: `1px solid ${uiNeutrals.surfaceBorder}`,
  color: "#111",
  "&:hover": { bgcolor: uiNeutrals.surfaceHover },
};

const LogoCarousel = () => {
  const navigate = useNavigate();
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [activeReviewIndex, setActiveReviewIndex] = useState(0);

  const handleBrowseDeals = () => {
    navigate("/deals");
  };

  const handleOpenReview = useCallback((index) => {
    setActiveReviewIndex(index);
    setReviewDialogOpen(true);
  }, []);

  const handleCloseReview = useCallback(() => setReviewDialogOpen(false), []);

  const stepReview = useCallback((delta) => {
    setActiveReviewIndex((i) => (i + delta + REVIEWS.length) % REVIEWS.length);
  }, []);

  useEffect(() => {
    if (!reviewDialogOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        stepReview(-1);
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        stepReview(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [reviewDialogOpen, stepReview]);

  const renderLogoTile = (tile) => (
    <Box
      key={tile.id}
      sx={{
        ...cardShellSx,
        backgroundColor: tile.bgColor,
        padding: "16px",
        "&:hover": {
          filter: "brightness(0.92)",
        },
      }}
    >
      <img
        src={tile.src}
        alt={tile.alt}
        style={{
          width: "60%",
          height: "60%",
          objectFit: "contain",
        }}
        onError={(e) => {
          const target = e.currentTarget;
          target.style.display = "none";
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML = `
              <div style="
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #999;
                font-size: 12px;
                text-align: center;
                padding: 8px;
              ">
                ${tile.alt}
              </div>
            `;
          }
        }}
      />
    </Box>
  );

  const renderTestimonialTile = (tile) => {
    const review = REVIEWS[tile.reviewIndex];
    if (!review) return null;

    return (
      <Box
        key={tile.id}
        role="button"
        tabIndex={0}
        onClick={() => handleOpenReview(tile.reviewIndex)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenReview(tile.reviewIndex);
          }
        }}
        sx={{
          ...cardShellSx,
          position: "relative",
          cursor: "pointer",
          bgcolor: alpha(appBackgroundColor, 0.52),
          border: `1px solid ${alpha(uiNeutrals.surfaceBorder, 0.5)}`,
          "&:hover": {
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.07)",
          },
          "&:focus-visible": {
            outline: `2px solid rgba(17, 24, 39, 0.35)`,
            outlineOffset: 2,
          },
        }}
        aria-label={`${review.name}: open full review`}
      >
        {/* Text layer behind */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            px: { xs: 1, md: 1.25 },
            py: { xs: 1, md: 1.25 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            pointerEvents: "none",
          }}
          aria-hidden
        >
          <Box
            sx={{
              filter: "blur(1px)",
              transform: "translateZ(0)",
              opacity: 1,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 0.65,
              userSelect: "none",
            }}
          >
            <Typography
              component="blockquote"
              sx={{
                margin: 0,
                fontSize: { xs: "0.55rem", md: "0.62rem" },
                color: "#4b5563",
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: { xs: 5, md: 6 },
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                fontStyle: "italic",
              }}
            >
              &ldquo;{review.quote}&rdquo;
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.55rem", md: "0.62rem" },
                fontWeight: 700,
                color: "#374151",
                lineHeight: 1.15,
              }}
            >
              {review.name}
            </Typography>
          </Box>
        </Box>

        {/* Loops logo in front */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            p: "16px",
            boxSizing: "border-box",
          }}
          aria-hidden
        >
          <Box
            component="img"
            src={LOOPS_LOGO_SRC}
            alt=""
            sx={{
              width: "60%",
              height: "60%",
              objectFit: "contain",
              filter:
                "drop-shadow(0 0 10px rgba(255, 255, 255, 0.5)) drop-shadow(0 1px 3px rgba(0, 0, 0, 0.12))",
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderTile = (tile) => {
    if (tile.kind === "logo") return renderLogoTile(tile);
    return renderTestimonialTile(tile);
  };

  const activeReview = REVIEWS[activeReviewIndex];

  return (
    <Box
      component="section"
      aria-labelledby="home-carousel-heading"
      sx={{
        width: "100%",
        maxWidth: 1040,
        mx: "auto",
        px: HOME_SECTION_HORIZONTAL_GUTTER,
        py: HOME_SECTION_BAND_PADDING_Y,
        bgcolor: "transparent",
      }}
    >
      <Typography
        component="h2"
        id="home-carousel-heading"
        sx={{
          ...HOME_SECTION_HEADING_SX,
          ...HOME_SECTION_HEADING_BELOW_MARGIN,
        }}
      >
        Join 2,000+ foodies finding deals today.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: { xs: 1, md: 2 },
          width: "100%",
          maxWidth: { xs: "min(500px, 100%)", md: "500px" },
          mx: "auto",
          mb: 4,
          justifyItems: "center",
        }}
      >
        {CAROUSEL_TILES.map((tile) => renderTile(tile))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CustomButton
          variant="secondary"
          endIcon={<NorthEastIcon sx={{ fontSize: "1.125rem" }} />}
          onClick={handleBrowseDeals}
          sx={{
            px: 3,
            py: 1.5,
          }}
        >
          Browse Deals
        </CustomButton>
      </Box>

      <Dialog
        open={reviewDialogOpen && Boolean(activeReview)}
        onClose={handleCloseReview}
        maxWidth="sm"
        fullWidth
        aria-labelledby="home-review-dialog-title"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "26px",
            border: `1px solid ${uiNeutrals.surfaceBorder}`,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.05)",
            bgcolor: appBackgroundColor,
            mx: { xs: 2, sm: "auto" },
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(5px) saturate(1.05)",
            WebkitBackdropFilter: "blur(5px) saturate(1.05)",
          },
        }}
      >
        <DialogTitle
          id="home-review-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pb: 1,
            fontSize: "1.05rem",
            fontWeight: 600,
            color: "#111",
          }}
        >
          Reviews
          <IconButton
            aria-label="Close dialog"
            onClick={handleCloseReview}
            edge="end"
            sx={closeIconButtonSx}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            pb: 3,
            px: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {activeReview && (
            <>
              <Box
                sx={{
                  textAlign: "center",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 2.5, sm: 3 },
                }}
              >
                <Typography
                  component="blockquote"
                  sx={{
                    margin: 0,
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    fontStyle: "italic",
                    color: "#374151",
                    lineHeight: 1.65,
                    maxWidth: 520,
                    mx: "auto",
                  }}
                >
                  &ldquo;{activeReview.quote}&rdquo;
                </Typography>
                <Typography
                  sx={{
                    mt: 2.5,
                    fontWeight: 600,
                    fontSize: { xs: "1.05rem", sm: "1.125rem" },
                    color: "#111827",
                  }}
                >
                  {activeReview.name}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  pt: 2.5,
                }}
              >
                <IconButton
                  aria-label="Previous review"
                  onClick={() => stepReview(-1)}
                  sx={reviewNavCircleSx}
                >
                  <ArrowBackIcon sx={{ fontSize: 22 }} />
                </IconButton>
                <IconButton
                  aria-label="Next review"
                  onClick={() => stepReview(1)}
                  sx={reviewNavCircleSx}
                >
                  <ArrowForwardIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LogoCarousel;
